import { useEffect, useState } from 'react';
import { sessionAuthService } from '@/services/session-auth-service';
import { FullPageLoader } from 'prosci-ui';

interface TokenAuthHandlerProps {
  children: React.ReactNode;
}

export function TokenAuthHandler({ children }: TokenAuthHandlerProps) {
  const [isProcessingToken, setIsProcessingToken] = useState(false);
  const [tokenProcessed, setTokenProcessed] = useState(false);
  const [tokenAuthenticated, setTokenAuthenticated] = useState(false);

  useEffect(() => {
    async function handleTokenAuth() {
      const win = window as Record<string, unknown>;

      // Prefer token already extracted by the inline script in index.html
      let rawToken: string | null = null;
      if (typeof win.__ADMIN_TOKEN__ === 'string') {
        rawToken = win.__ADMIN_TOKEN__ as string;
        delete win.__ADMIN_TOKEN__;
      } else {
        const urlParams = new URLSearchParams(window.location.search);
        rawToken = urlParams.get('jwt') || urlParams.get('token') || urlParams.get('access');

        if (rawToken) {
          urlParams.delete('jwt');
          urlParams.delete('token');
          urlParams.delete('access');
          const newUrl = `${window.location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
          window.history.replaceState({}, '', newUrl);
        }
      }

      if (rawToken && !tokenProcessed) {
        setIsProcessingToken(true);

        try {
          await sessionAuthService.authenticateLicenseAdmin(rawToken);

          setTokenProcessed(true);

          // Small delay to ensure session is fully stored
          setTimeout(() => {
            setTokenAuthenticated(true);
          }, 50);
        } catch (error) {
          console.error('[TokenAuthHandler] Token authentication failed:', error);
        } finally {
          setIsProcessingToken(false);
        }
      }
    }

    handleTokenAuth();
  }, [tokenProcessed]);

  // Force re-render after token authentication
  useEffect(() => {
    if (tokenAuthenticated) {
      // Session is set, React will handle the re-render
    }
  }, [tokenAuthenticated]);

  const win = window as Record<string, unknown>;
  const urlParams = new URLSearchParams(window.location.search);
  const hasTokenInUrl = typeof win.__ADMIN_TOKEN__ === 'string' ||
    urlParams.has('jwt') || urlParams.has('token') || urlParams.has('access');

  if (isProcessingToken || (hasTokenInUrl && !tokenProcessed)) {
    return <FullPageLoader message="Authenticating..." />;
  }

  return <>{children}</>;
}